import { maxArrayLike, sumArrayLike } from "$root/utils";
import { useDeepEqualMemo } from "@mdotm/shadcn/legacy/react-extensions";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import type { QueryObserverResult } from "@tanstack/query-core";
import type { DependencyList } from "react";


export function useMappedQueries<TDataChunks extends [unknown, ...unknown[]], TMapped>(
	mapFn: (data: TDataChunks) => TMapped,
	deps: DependencyList,
	queries: {
		[K in keyof TDataChunks]: QueryObserverResult<TDataChunks[K], unknown>;
	}
): QueryObserverResult<TMapped, unknown> {
	const mapFnRef = useUnsafeUpdatedRef(mapFn);
	const queriesState = queries.flatMap((query) => [
		query.status,
		query.dataUpdatedAt,
		query.fetchStatus,
		query.isPaused,
		query.isRefetching,
	])
	// biome-ignore lint/correctness/useExhaustiveDependencies: <controlled memoization>
	return useDeepEqualMemo(() => {
		return {
			dataUpdatedAt: maxArrayLike(queries, (q) => q.dataUpdatedAt),
			error: queries.find((q) => q.error)?.error,
			errorUpdateCount: sumArrayLike(queries, (q) => q.errorUpdateCount),
			errorUpdatedAt: maxArrayLike(queries, (q) => q.errorUpdatedAt),
			failureCount: sumArrayLike(queries, (q) => q.failureCount),
			failureReason: queries.find((q) => q.failureReason)?.failureReason,
			fetchStatus: queries.find((q) => q.fetchStatus === "fetching" || q.fetchStatus === "paused")?.fetchStatus ?? "idle",
			isFetching: queries.some((q) => q.isFetching),
			isError: queries.some((q) => q.isError),

			isLoading: queries.some((q) => q.isLoading),
			isLoadingError: queries.some((q) => q.isLoadingError),
			isRefetchError: queries.some((q) => q.isRefetchError),
			isSuccess: queries.every((q) => q.isSuccess),
			status: queries.find((q) => q.status === "error" || q.status === "loading")?.status ?? "success",

			data: queries.every((q) => q.data !== undefined)
				? mapFnRef.current(queries.map((q) => q.data!) as TDataChunks)
				: undefined,
			refetch: (options) => Promise.all(queries.map((q) => q.refetch(options))).then((fetched) => fetched.every((q) => q.data !== undefined)
				? mapFnRef.current(Object.values(fetched).map((q) => q.data!) as TDataChunks)
				: undefined
			),
		} as QueryObserverResult<TMapped, unknown>;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mapFnRef, queriesState, ...deps]);
}
